/**
 * Request schemas shared by the API (validation) and the web app (forms),
 * so both sides always agree on what is valid.
 */
import { z } from 'zod';
import { CATEGORIES, DEPARTMENTS, OFFICE_LOCATIONS, PRIORITIES, SERVICE_MODES, STATUSES } from './domain.ts';

/** Accepts real booleans (JSON) and 'true'/'false' strings (multipart forms). */
const boolish = z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean());

const trimmed = (max: number, required: string) =>
  z.string().trim().min(1, required).max(max, `Must be ${max} characters or fewer.`);

const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${max} characters or fewer.`)
    .optional()
    .transform((v) => (v ? v : undefined));

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email('Please enter a valid email address.').max(254));

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters long.')
  .max(200)
  .regex(/[a-z]/, 'Password must include a lowercase letter.')
  .regex(/[A-Z]/, 'Password must include an uppercase letter.')
  .regex(/[0-9]/, 'Password must include a number.');

const phoneSchema = z
  .string()
  .trim()
  .max(25, 'Phone number is too long.')
  .regex(/^[0-9+\-\s()]*$/, 'Phone number may only contain digits, spaces and + - ( ).')
  .optional()
  .transform((v) => (v ? v : undefined));

// ── Auth ────────────────────────────────────────────────────────────

export const magicLinkRequestSchema = z.object({ email: emailSchema });

export const magicLinkVerifySchema = z.object({ token: z.string().min(20).max(200) });
/** The 6-digit code from the sign-in email, typed into the app instead of opening the link. */
export const magicLinkCodeSchema = z.object({
  email: emailSchema,
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'The code is the 6 digits from the email.'),
});

export const loginSchema = z.object({
  /** Username or email. */
  login: trimmed(254, 'Please enter your username.'),
  password: z.string().min(1, 'Please enter your password.').max(200),
});

export const setupSchema = z.object({
  name: trimmed(100, 'Please enter your name.'),
  email: emailSchema,
  username: z
    .string()
    .trim()
    .min(3, 'Username must be 3–30 characters.')
    .max(30, 'Username must be 3–30 characters.')
    .regex(/^[a-zA-Z0-9_.-]+$/, 'Username may only contain letters, numbers and _ . -'),
  password: passwordSchema,
});

/** A code from the authenticator app (6 digits) or a recovery code. */
export const twoFactorCodeSchema = z
  .string()
  .trim()
  .min(6, 'Enter the 6-digit code from your authenticator app.')
  .max(20, 'That doesn’t look like a code.');

export const twoFactorLoginSchema = z.object({
  challenge: z.string().min(20).max(200),
  code: twoFactorCodeSchema,
});

export const twoFactorSetupSchema = z.object({
  password: z.string().min(1, 'Please enter your password.').max(200),
});

export const twoFactorConfirmSchema = z.object({ code: twoFactorCodeSchema });

export const twoFactorDisableSchema = z.object({
  password: z.string().min(1, 'Please enter your password.').max(200),
  code: twoFactorCodeSchema,
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Please enter your current password.'),
  newPassword: passwordSchema,
});

export const profileSchema = z.object({
  name: trimmed(100, 'Please enter your name.'),
  department: z.enum(DEPARTMENTS).nullable().optional(),
  officeLocation: z.enum(OFFICE_LOCATIONS).nullable().optional(),
  phone: phoneSchema.nullable(),
});

// ── Users (admin) ───────────────────────────────────────────────────

export const newItUserSchema = setupSchema.extend({
  role: z.enum(['technician', 'admin']),
});

export const userUpdateSchema = z
  .object({
    name: trimmed(100, 'Please enter a name.').optional(),
    /** Where the person's sign-in links, replies and alerts go. */
    email: emailSchema.optional(),
    role: z.enum(['staff', 'technician', 'admin']).optional(),
    active: z.boolean().optional(),
    /** Admin-set password for an IT account. */
    password: passwordSchema.optional(),
    /** Turn off someone's two-step sign-in (e.g. they lost their phone). */
    resetTwoFactor: z.literal(true).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update.');

// ── Tickets ─────────────────────────────────────────────────────────

export const newTicketSchema = z.object({
  /** Required for guests; ignored for signed-in staff (their account is used). */
  name: optionalTrimmed(100),
  email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
  department: z.enum(DEPARTMENTS, 'Please choose your department.'),
  officeLocation: z.enum(OFFICE_LOCATIONS, 'Please choose your office location.'),
  phone: phoneSchema,
  category: z.enum(CATEGORIES, 'Please choose the type of problem.'),
  subject: trimmed(120, 'Please enter a short subject.'),
  description: trimmed(2000, 'Please describe the problem.'),
  serviceMode: z.enum(SERVICE_MODES, 'Please choose how you would like to be helped.'),
  /** Staff's own phone or a personal favour: no deadline, handled in the personal-help slot. */
  personal: boolish.default(false),
});
export type NewTicketInput = z.infer<typeof newTicketSchema>;

export const replySchema = z.object({
  body: trimmed(5000, 'Please write a message.'),
  /** Internal notes are visible to IT only. */
  internal: boolish.default(false),
});

export const ticketUpdateSchema = z
  .object({
    status: z.enum(STATUSES).optional(),
    priority: z.enum(PRIORITIES).optional(),
    assigneeId: z.number().int().positive().nullable().optional(),
    /** Required when moving to resolved. */
    resolutionNotes: z.string().trim().max(5000).optional(),
    personal: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.status !== undefined ||
      v.priority !== undefined ||
      v.assigneeId !== undefined ||
      v.personal !== undefined,
    'Nothing to update.',
  );
export type TicketUpdateInput = z.infer<typeof ticketUpdateSchema>;

export const voidTicketSchema = z.object({
  reason: trimmed(500, 'Please say why the ticket is being voided.').pipe(
    z.string().min(3, 'Please say why the ticket is being voided.'),
  ),
});

const csv = <T extends readonly [string, ...string[]]>(values: T) =>
  z
    .string()
    .optional()
    .transform((v) => (v ? v.split(',').filter(Boolean) : undefined))
    .pipe(z.array(z.enum(values)).optional());

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.')
  .optional();

export const ticketListQuerySchema = z.object({
  status: csv(STATUSES),
  category: csv(CATEGORIES),
  priority: csv(PRIORITIES),
  department: z.enum(DEPARTMENTS).optional(),
  office: z.enum(OFFICE_LOCATIONS).optional(),
  /** 'me', 'none', or a user id. */
  assignee: z.union([z.enum(['me', 'none']), z.coerce.number().int().positive()]).optional(),
  q: z.string().trim().max(100).optional(),
  from: isoDate,
  to: isoDate,
  overdue: boolish.optional(),
  /** Personal requests: only them, or leave them out. */
  personal: z.enum(['only', 'exclude']).optional(),
  sort: z.enum(['newest', 'oldest', 'updated', 'due']).default('newest'),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type TicketListQuery = z.infer<typeof ticketListQuerySchema>;

// ── Feedback ────────────────────────────────────────────────────────

const rating = (label: string) =>
  z.coerce.number(`Please rate ${label}.`).int().min(1, `Please rate ${label}.`).max(5);

/** An optional 1–5 rating; an empty form field means "not answered". */
const optionalRating = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z.coerce.number().int().min(1).max(5).optional(),
);

/**
 * Only the overall rating is required: it's what the one-tap stars in the
 * resolution email send. The rest is extra detail people may add.
 */
export const feedbackSchema = z.object({
  satisfaction: rating('your overall satisfaction'),
  responseTime: optionalRating,
  resolutionQuality: optionalRating,
  professionalism: optionalRating,
  wouldRecommend: z.preprocess((v) => (v === '' || v === null ? undefined : v), boolish.optional()),
  comments: optionalTrimmed(1000),
});
export type FeedbackInput = z.infer<typeof feedbackSchema>;

// ── Knowledge base ──────────────────────────────────────────────────

export const kbArticleSchema = z.object({
  title: trimmed(150, 'Please enter a title.'),
  category: z.enum(CATEGORIES).nullable().default(null),
  body: trimmed(10_000, 'Please write the article.'),
  published: z.boolean().default(false),
});
export type KbArticleInput = z.infer<typeof kbArticleSchema>;

// ── Announcements ───────────────────────────────────────────────────

export const ANNOUNCEMENT_TONES = ['outage', 'notice'] as const;
export type AnnouncementTone = (typeof ANNOUNCEMENT_TONES)[number];

/** A banner for everyone, e.g. "Internet is down at FTC — we're on it". */
export const announcementSchema = z.object({
  message: trimmed(300, 'Please write the announcement.'),
  /** Only mentions this office; null means everywhere. */
  office: z.enum(OFFICE_LOCATIONS).nullable().default(null),
  tone: z.enum(ANNOUNCEMENT_TONES).default('outage'),
  /** When it disappears by itself; null means when IT ends it. */
  endsAt: z.iso.datetime().nullable().default(null),
});
export type AnnouncementInput = z.infer<typeof announcementSchema>;

// ── Saved replies (IT) ──────────────────────────────────────────────

/**
 * Text IT sends often. {name} (the requester's first name), {ticket} (the
 * ticket number) and {me} (your first name) are filled in when inserted.
 */
export const savedReplySchema = z.object({
  title: trimmed(80, 'Please give it a short name.'),
  body: trimmed(5000, 'Please write the reply.'),
});
export type SavedReplyInput = z.infer<typeof savedReplySchema>;

/** Fill in a saved reply's placeholders. Unknown ones are left as they are. */
export function fillSavedReply(body: string, values: { name: string; ticket: string; me: string }): string {
  return body.replace(/\{(name|ticket|me)\}/g, (_, key: keyof typeof values) => values[key]);
}
